import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Star, Trophy, MapPin, Heart, Send, CheckCircle, Clock, XCircle, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface Nomination {
  id: string;
  player_name: string;
  player_username: string | null;
  sport: string;
  position: string | null;
  school: string | null;
  state_province: string;
  nomination_reason: string;
  nominator_user_id: string | null;
  status: string;
  created_at: string;
}

interface NominationResponse {
  id: string;
  nomination_id: string;
  response_type: string;
  custom_message: string | null;
  admin_approval_status: string;
  admin_rejection_reason: string | null;
  created_at: string;
}

export default function MyNominations() {
  const { user } = useUnifiedUser();
  const navigate = useNavigate();
  const [nominations, setNominations] = useState<Nomination[]>([]);
  const [responses, setResponses] = useState<NominationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [customMessages, setCustomMessages] = useState<Record<string, string>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Find nominations where this user is the nominated player
      const username = user.username;
      const { data: noms } = await (supabase as any)
        .from('player_nominations')
        .select('*')
        .eq('status', 'approved')
        .or(`player_username.eq.@${username},player_username.eq.${username}`)
        .order('created_at', { ascending: false });

      setNominations(noms || []);

      // Load existing responses from this user
      const { data: resps } = await (supabase as any)
        .from('nomination_responses')
        .select('*')
        .eq('responder_user_id', user.id)
        .order('created_at', { ascending: false });

      setResponses(resps || []);
    } catch (err) {
      console.error('Error loading nominations:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasResponded = (nominationId: string) => {
    return responses.some(r => r.nomination_id === nominationId);
  };

  const getResponse = (nominationId: string) => {
    return responses.find(r => r.nomination_id === nominationId);
  };

  const sendDefaultThankYou = async (nomination: Nomination) => {
    if (!user) return;
    setSendingId(nomination.id);
    try {
      // Insert default thank you - auto-approved
      await (supabase as any).from('nomination_responses').insert({
        nomination_id: nomination.id,
        responder_user_id: user.id,
        responder_user_type: user.userType,
        response_type: 'default_thank_you',
        admin_approval_status: 'approved',
        reviewed_at: new Date().toISOString(),
      });

      // Send notification directly to the nominator (default messages are auto-approved)
      if (nomination.nominator_user_id) {
        await supabase.from('notifications').insert({
          user_id: nomination.nominator_user_id,
          user_type: 'internal',
          to_user_id: nomination.nominator_user_id,
          notification_type: 'nomination_thank_you',
          category: 'nomination',
          title: '💝 Thank You for Your Nomination!',
          message: `${nomination.player_name} says: "Thank You for Nominating Me, I really appreciate it!"`,
          priority: 'medium',
          requires_action: false,
        });
      }

      toast.success('Thank you message sent!');
      loadData();
    } catch (err) {
      console.error('Error sending thank you:', err);
      toast.error('Failed to send thank you');
    } finally {
      setSendingId(null);
    }
  };

  const sendCustomThankYou = async (nomination: Nomination) => {
    if (!user) return;
    const message = customMessages[nomination.id];
    if (!message?.trim()) {
      toast.error('Please write a custom message');
      return;
    }
    setSendingId(nomination.id);
    try {
      // Custom messages need admin approval
      await (supabase as any).from('nomination_responses').insert({
        nomination_id: nomination.id,
        responder_user_id: user.id,
        responder_user_type: user.userType,
        response_type: 'custom',
        custom_message: message,
        admin_approval_status: 'pending',
      });

      // Notify admin about pending review
      await supabase.from('admin_notifications').insert({
        notification_type: 'nomination_response_review',
        title: 'Custom Thank You Message Pending Review',
        message: `${user.username} sent a custom thank you message for their nomination that needs review.`,
        priority: 'medium',
      });

      toast.success('Custom message submitted for admin review. You will be notified once approved.');
      setCustomMessages(prev => ({ ...prev, [nomination.id]: '' }));
      loadData();
    } catch (err) {
      console.error('Error sending custom thank you:', err);
      toast.error('Failed to submit message');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
        <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <Star className="h-7 w-7 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">My Nominations</h1>
        </div>

        {loading ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">Loading...</CardContent></Card>
        ) : nominations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">No Nominations Yet</p>
              <p className="text-muted-foreground">When someone nominates you and it's approved, it will appear here.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {nominations.map(nom => {
              const responded = hasResponded(nom.id);
              const existingResponse = getResponse(nom.id);
              
              return (
                <Card key={nom.id} className="border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Star className="h-5 w-5 text-yellow-500" />
                      You were nominated!
                    </CardTitle>
                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" />{nom.sport}{nom.position && ` • ${nom.position}`}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{nom.state_province}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{new Date(nom.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-muted/50 p-3 rounded-md">
                      <p className="text-sm font-medium text-foreground mb-1">Why you were nominated:</p>
                      <p className="text-sm text-muted-foreground">{nom.nomination_reason}</p>
                    </div>

                    {responded ? (
                      <div className="p-3 rounded-md border border-border bg-muted/30">
                        <div className="flex items-center gap-2 mb-1">
                          {existingResponse?.admin_approval_status === 'approved' && (
                            <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Thank You Sent</Badge>
                          )}
                          {existingResponse?.admin_approval_status === 'pending' && (
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500"><Clock className="h-3 w-3 mr-1" />Pending Admin Review</Badge>
                          )}
                          {existingResponse?.admin_approval_status === 'rejected' && (
                            <>
                              <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Message Rejected</Badge>
                              {existingResponse.admin_rejection_reason && (
                                <p className="text-xs text-destructive mt-1">Reason: {existingResponse.admin_rejection_reason}</p>
                              )}
                            </>
                          )}
                        </div>
                        {existingResponse?.custom_message && (
                          <p className="text-xs text-muted-foreground mt-2">Your message: "{existingResponse.custom_message}"</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3 pt-3 border-t border-border">
                        <p className="text-sm font-medium text-foreground flex items-center gap-2">
                          <Heart className="h-4 w-4 text-red-500" /> Say Thank You
                        </p>
                        
                        <Button
                          size="sm"
                          className="w-full bg-green-600 hover:bg-green-700"
                          disabled={sendingId === nom.id}
                          onClick={() => sendDefaultThankYou(nom)}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send "Thank You for Nominating Me, I really appreciate it!"
                        </Button>

                        <div className="text-center text-xs text-muted-foreground">— or send a custom message (requires admin approval) —</div>

                        <Textarea
                          placeholder="Write your custom thank you message..."
                          value={customMessages[nom.id] || ''}
                          onChange={e => setCustomMessages(prev => ({ ...prev, [nom.id]: e.target.value }))}
                          className="text-sm"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={sendingId === nom.id || !customMessages[nom.id]?.trim()}
                          onClick={() => sendCustomThankYou(nom)}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Submit Custom Message for Review
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
